import { useMemo } from 'react';
import { useGPS } from '../contexts/GPSContext.jsx';
import { getIsoWeekNumber } from '../utils/isoDates.js';

function formatDateUTC(date) {
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatMonthUTC(date) {
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Buckets GPS issues over the selected period for the evolution chart.
 * Extracted from GPSIssuesEvolutionGraph so both the legacy graph and the new
 * GPSEvolutionCard share a single source of truth.
 *
 * Returns:
 *  - chartData: [{ label, created, resolved, abandoned }]
 *  - activeChartData: [{ label, active }]  (open backlog at end of each bucket)
 *  - mode: 'daily' | 'weekly' | 'monthly'
 */
export const useGPSEvolutionData = (stats) => {
  const gps = useGPS();

  return useMemo(() => {
    if (!gps?.startDate || !gps?.endDate || !stats) {
      return { chartData: [], activeChartData: [], mode: 'daily' };
    }

    const start = new Date(gps.startDate);
    const end = new Date(gps.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let mode = 'weekly';
    if (diffDays <= 30) mode = 'daily';
    else if (diffDays > 120) mode = 'monthly';

    const createdCounts = new Map();
    const resolvedCounts = new Map();
    const abandonedCounts = new Map();
    const bucketEndDates = new Map();

    if (mode === 'daily') {
      const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
      const endUTC = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
      while (cursor <= endUTC) {
        const key = formatDateUTC(cursor);
        createdCounts.set(key, 0);
        resolvedCounts.set(key, 0);
        abandonedCounts.set(key, 0);
        const endOfDay = new Date(cursor);
        endOfDay.setUTCHours(23, 59, 59, 999);
        bucketEndDates.set(key, endOfDay.getTime());
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
    } else if (mode === 'monthly') {
      const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
      const endUTC = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
      while (cursor <= endUTC) {
        const key = formatMonthUTC(cursor);
        createdCounts.set(key, 0);
        resolvedCounts.set(key, 0);
        abandonedCounts.set(key, 0);
        const endOfMonth = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0, 23, 59, 59, 999));
        bucketEndDates.set(key, endOfMonth.getTime());
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
      }
    } else {
      const set = new Set();
      const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
      const endUTC = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
      while (cursor <= endUTC) {
        const weekKey = getIsoWeekNumber(cursor).fullWeekString;
        set.add(weekKey);
        const endOfDay = new Date(cursor);
        endOfDay.setUTCHours(23, 59, 59, 999);
        bucketEndDates.set(weekKey, endOfDay.getTime());
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
      Array.from(set.values()).forEach((k) => {
        createdCounts.set(k, 0);
        resolvedCounts.set(k, 0);
        abandonedCounts.set(k, 0);
      });
    }

    const getKey = (dateStr) => {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return null;
      if (mode === 'daily') return formatDateUTC(d);
      if (mode === 'monthly') return formatMonthUTC(d);
      return getIsoWeekNumber(d).fullWeekString;
    };

    (stats.openTickets || []).forEach((issue) => {
      const key = getKey(issue.created);
      if (key && createdCounts.has(key)) createdCounts.set(key, (createdCounts.get(key) || 0) + 1);
    });

    (stats.closedTickets || []).forEach((issue) => {
      const key = getKey(issue.status_date);
      if (key && resolvedCounts.has(key)) resolvedCounts.set(key, (resolvedCounts.get(key) || 0) + 1);
    });

    (stats.abandonedTickets || []).forEach((issue) => {
      const key = getKey(issue.created);
      if (key && abandonedCounts.has(key)) abandonedCounts.set(key, (abandonedCounts.get(key) || 0) + 1);
    });

    const labels = Array.from(
      new Set([...createdCounts.keys(), ...resolvedCounts.keys(), ...abandonedCounts.keys()])
    ).sort((a, b) => a.localeCompare(b));

    let evolutionData = labels.map((label) => ({
      label,
      created: createdCounts.get(label) || 0,
      resolved: resolvedCounts.get(label) || 0,
      abandoned: abandonedCounts.get(label) || 0,
    }));

    const activeData = labels.map((label) => {
      const endDate = bucketEndDates.get(label);
      if (!endDate) return { label, active: 0 };
      const activeCount = (gps.summaryIssues || []).reduce((acc, issue) => {
        const created = new Date(issue.created).getTime();
        const closed = issue.status_category === 'DONE' && issue.status_date ? new Date(issue.status_date).getTime() : null;
        const abandoned = issue.status === 'Annulé' ? (issue.status_date ? new Date(issue.status_date).getTime() : (issue.updated ? new Date(issue.updated).getTime() : null)) : null;
        if (created <= endDate) {
          const isClosed = closed && closed <= endDate;
          const isAbandoned = abandoned && abandoned <= endDate;
          if (!isClosed && !isAbandoned) return acc + 1;
        }
        return acc;
      }, 0);
      return { label, active: activeCount };
    });

    if (mode !== 'daily') {
      const firstIdx = evolutionData.findIndex((d) => (d.created || 0) > 0 || (d.resolved || 0) > 0 || (d.abandoned || 0) > 0);
      const lastIdx = (() => {
        for (let i = evolutionData.length - 1; i >= 0; i--) {
          const d = evolutionData[i];
          if ((d.created || 0) > 0 || (d.resolved || 0) > 0 || (d.abandoned || 0) > 0) return i;
        }
        return -1;
      })();
      if (firstIdx === -1 || lastIdx === -1 || lastIdx < firstIdx) evolutionData = [];
      else evolutionData = evolutionData.slice(firstIdx, lastIdx + 1);
    }

    return { chartData: evolutionData, activeChartData: activeData, mode };
  }, [gps.startDate, gps.endDate, stats, gps.summaryIssues]);
};
